import { ColDef } from '@ag-grid-community/core';
import { DealStatusEnum, FilterPill, LEGACY_NOT_PUBLISHED_STATUS, operandConversion } from './deals.types';
import {
  getForwardCalendarMonday,
  getNearestMonday,
  getUTCDateFromUSFormatted,
} from '../../utils/dates';
import { IssuerRating, Rating } from '../../types';
import { DateTime } from 'luxon';

const UTC_DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const TBD_LABEL = 'TBD';

/** Labels used by the pricing-date day-of-week filter (must match `DayOfWeekFilter`). */
export const PRICING_DAY_FILTER_LABELS: readonly string[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  TBD_LABEL,
];

export function getDisplayDealStatus(
  dealStatus: string,
  dealType: string | null | undefined,
  tradeDate: string | number | null | undefined,  // Accept both string and number
  currentDateTime: DateTime = DateTime.now()
): string {
  const normalizedDealStatus =
    dealStatus?.trim().toLowerCase() === LEGACY_NOT_PUBLISHED_STATUS.toLowerCase()
      ? DealStatusEnum.EXPECTED
      : dealStatus;

  // If status is not "Allotments Available", return as-is
  if (normalizedDealStatus !== DealStatusEnum.ALLOTMENTS_AVAILABLE) {
    return normalizedDealStatus;
  }

  // If trade date is not available, return "Allotments Available"
  if (!tradeDate) {
    return DealStatusEnum.ALLOTMENTS_AVAILABLE;
  }

  // Parse trade date - handle both ISO string and timestamp number
  let tradeDateTime: DateTime;
  if (typeof tradeDate === 'number') {
    tradeDateTime = DateTime.fromMillis(tradeDate);
  } else {
    tradeDateTime = DateTime.fromISO(tradeDate);
  }
  
  const isTradeDatePassed = currentDateTime > tradeDateTime;

  // If trade date hasn't passed, return "Allotments Available"
  if (!isTradeDatePassed) {
    return DealStatusEnum.ALLOTMENTS_AVAILABLE;
  }

  // Trade date has passed - return appropriate status based on deal type
  if (dealType === 'Negotiated') {
    return DealStatusEnum.PRICED;
  } else if (dealType === 'Competitive') {
    return DealStatusEnum.SOLD;
  }

  // Fallback to original status
  return DealStatusEnum.ALLOTMENTS_AVAILABLE;
}

export const getNumberFilterConditionLabel = (condition): string => {
  return `${operandConversion[condition.type] || condition.type} ${condition.filter}`;
};

/**
 * Builds dataserver criteria for the deals listing: date range and optionally open-tab deal IDs.
 * Open-tab deals are included in the subscription for real-time updates but are filtered client-side
 * to only show when they belong to the current week.
 */
export const getDealsDatasourceCriteria = (
  dealsDate: string,
  forwardCalendar?: boolean,
  extraDealIds?: (string | number)[],
): string => {
  let base: string;
  if (!forwardCalendar && dealsDate) {
    const date = getUTCDateFromUSFormatted(dealsDate);
    const friday = new Date(date);
    friday.setUTCDate(friday.getUTCDate() + 4);
    // TBD deals (null expected pricing date) are excluded from current and historic weeks
    base = `EXPECTED_PRICING_DATE >= ${date.getTime()} && EXPECTED_PRICING_DATE <= ${friday.getTime()}`;
  } else {
    // TBD deals (null expected pricing date) are included in Forward Calendar
    base = `EXPECTED_PRICING_DATE >= ${getForwardCalendarMonday().getTime()} || EXPECTED_PRICING_DATE == null`;
  }
  if (extraDealIds?.length) {
    const dealIdClause = extraDealIds
      .map((id) => (typeof id === 'number' ? `DEAL_ID == ${id}` : `DEAL_ID == "${String(id)}"`))
      .join(' || ');
    return `(${base}) || (${dealIdClause})`;
  }
  return base;
};

/**
 * Floors a UTC millisecond timestamp to the start of its UTC calendar day.
 * Used so that sort comparisons are date-only (time-of-day differences are ignored).
 */
export function toUTCDateOnly(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Stable partition: rows with EXPECTED_PRICING_DATE first, null/empty last.
 * Mutates `nodes` in place (AG Grid postSortRows contract).
 */
export function reorderNodesNullExpectedPricingDateLast(
  nodes: { data?: { EXPECTED_PRICING_DATE?: unknown } | null }[],
): void {
  if (!nodes.length) {
    return;
  }
  const withDate: typeof nodes = [];
  const withoutDate: typeof nodes = [];
  for (const node of nodes) {
    const v = node.data?.EXPECTED_PRICING_DATE;
    if (v == null || v === '') {
      withoutDate.push(node);
    } else {
      withDate.push(node);
    }
  }
  nodes.length = 0;
  nodes.push(...withDate, ...withoutDate);
}

export const isCurrentWeek = (dealsDate: string): boolean => getNearestMonday() === dealsDate;

// Mapping of field names to display names for filter pills
const FIELD_DISPLAY_NAMES: { [key: string]: string } = {
  'EXPECTED_PRICING_DATE': 'Date',
  'DEAL_TYPE': 'Deal Type',
  'DEAL_SIZE': 'Par ($M)',
  'DEAL_DESCRIPTION': 'Issue Description',
  'STATE': 'State',
  'TAX_STATUS': 'Tax Status',
  'DEAL_MOODYS_RATING': "Moody's",
  'DEAL_SP_RATING': "S&P",
  'DEAL_FITCH_RATING': 'Fitch',
  'DEAL_KROLL_RATING': 'Kroll',
  'ISSUER_RATING': 'M/S/F/K',
  'DEAL_SECTOR': 'Sector',
  'SHORT_NAME': 'Lead',
  'DEAL_STATUS': 'Deal Status',
};

/** Optional universe per column for concise “Excluded: …” filter pills (set filters). */
export type SetFilterUniverses = Partial<Record<string, readonly string[]>>;

/** Per-column "all selected" labels; falls back to 'All' when not listed. */
const SET_FILTER_ALL_SELECTED_LABELS: Partial<Record<string, string>> = {
  STATE: 'All states',
};

/**
 * When most of a finite set is selected, show `Excluded: a, b` instead of a long included list.
 */
export function formatSetFilterLabelWithExclusion(
  values: string[] | undefined,
  universe: readonly string[],
  allSelectedLabel: string,
): string {
  if (!values?.length) {
    return '';
  }
  if (universe.length === 0) {
    return values.join(', ');
  }
  const selectedSet = new Set(values);
  if (universe.every((u) => selectedSet.has(u))) {
    return allSelectedLabel;
  }
  const unselected = universe.filter((u) => !selectedSet.has(u));
  if (unselected.length > 0 && unselected.length < values.length) {
    unselected.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return `Excluded: ${unselected.join(', ')}`;
  }
  return values.join(', ');
}

export const createFilterPills = (
  model: { [p: string]: any },
  setFilterUniverses: SetFilterUniverses = {},
): FilterPill[] => {
  if (!model) {
    return [];
  }
  return Object.keys(model)
    .map((key) => {
      const modelItem = model[key];

      let label = '';
      if (key === 'EXPECTED_PRICING_DATE') {
        const values = modelItem.values;
        if (!values?.length || values.length === PRICING_DAY_FILTER_LABELS.length) {
          label = null;
        } else {
          label = formatSetFilterLabelWithExclusion(values, PRICING_DAY_FILTER_LABELS, 'All days');
        }
      } else if (modelItem.filterType === 'number') {
        if (modelItem.conditions?.length) {
          label = modelItem.conditions.map(getNumberFilterConditionLabel).join(', ');
        } else {
          label = getNumberFilterConditionLabel(modelItem);
        }
      } else if (modelItem.filterType === 'set') {
        const universe = setFilterUniverses[key];
        const allLabel = SET_FILTER_ALL_SELECTED_LABELS[key] ?? 'All';
        label =
          universe?.length > 0
            ? formatSetFilterLabelWithExclusion(modelItem.values, universe, allLabel)
            : (modelItem.values ?? []).join(', ');
      } else {
        label = modelItem.filter;
      }

      const displayName = FIELD_DISPLAY_NAMES[key] || key;
      const fullLabel = label ? `${displayName}: ${label}` : null;

      return {
        key,
        label: fullLabel,
      };
    })
    .filter((p) => !!p.label);
};

const isNotThisWeek = (timestamp: number): boolean => {
  const now = new Date();

  const startOfThisWeek = new Date(now);
  // Week boundaries in UTC to keep date-only fields consistent across user timezones.
  startOfThisWeek.setUTCDate(now.getUTCDate() - now.getUTCDay());
  startOfThisWeek.setUTCHours(0, 0, 0, 0);

  const startOfNextWeek = new Date(startOfThisWeek);
  startOfNextWeek.setUTCDate(startOfThisWeek.getUTCDate() + 7);

  return timestamp < startOfThisWeek.getTime() || timestamp >= startOfNextWeek.getTime();
};

export const formatPricingDate = (timestamp: number, forwardCalendar?: boolean) => {
  if (timestamp === null) {
    return TBD_LABEL;
  }

  const date = new Date(timestamp);

  const dayNumber = date.getUTCDate();
  const monthNumber = date.getUTCMonth() + 1;
  const dayShort = UTC_DAYS_SHORT[date.getUTCDay()];

  if (forwardCalendar && isNotThisWeek(timestamp)) {
    return `${monthNumber}/${dayNumber}`;
  } else {
    return `${dayShort} ${monthNumber}/${dayNumber}`;
  }
};

export const formatPricingDateForTooltip = (timestamp: number | null): string => {
  if (timestamp === null) {
    return TBD_LABEL;
  }

  const date = new Date(timestamp);
  const dayNumber = date.getUTCDate();
  const monthNumber = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  const dayShort = UTC_DAYS_SHORT[date.getUTCDay()];
  return `${dayShort} ${monthNumber}/${dayNumber}/${year}`;
};

export const getFixedWidthConfig = (
  width: number,
): Pick<ColDef, 'width' | 'minWidth' | 'maxWidth'> => ({
  width,
  minWidth: width,
  maxWidth: width,
});

//ToDo still waiting on confirmation on values to show icon.
export const isBofaLead = (value: string): boolean => {
  if (!value) {
    return false;
  }

  return value.indexOf('BofA Securities') !== -1;
};

export const renderSyndicateCell = (params: any): string => {
  const shortName = params?.data?.SHORT_NAME;
  const syndicate = params?.data?.SYNDICATE;
  const displayValue = shortName || syndicate?.split(';')?.[0]?.trim() || '';
  if (!displayValue) {
    return `<span></span>`;
  }
  if (isBofaLead(displayValue)) {
    return `<img alt="Bank of America logo" style="height: 10px; margin-right: 10px" src="/logos/bofa-white.svg"><span>BofA</span>`;
  } else {
    return `<span>${displayValue}</span>`;
  }
}

export const checkIsForwardCalendar = (dateStr: string): boolean => {
  if (!dateStr) {
    return false;
  }
  const date = getUTCDateFromUSFormatted(dateStr);
  const currentWeekDate = getUTCDateFromUSFormatted(getNearestMonday());
  return date > currentWeekDate;
};

const STANDARD_RATING_FILTER_SUFFIX = ['Sub-IG', 'NR', 'Various'] as const;

export function formatRatingFilterLabel(value: string): string {
  if (!value) {
    return 'Blank';
  }
  return value === 'Various' ? 'VAR' : value;
}

export function getIssuerCreditRatingFilterOptions(ratings: Rating[]): string[] {
  const investmentGradeRatings = ratings
    .filter((r) => r.INVESTMENT_GRADE === true)
    .sort((left, right) => left.ORDER_NUM - right.ORDER_NUM)
    .map((r) => r.CREDIT_RATING);
  return ['', ...investmentGradeRatings, ...STANDARD_RATING_FILTER_SUFFIX];
}

export function getIssuerRatingLabel(ratings: Rating[], value: string): string {
  const normalized = normalizeRatingFilterLabel(value);
  if (isGenericRatingFilterLabel(normalized)) {
    return normalized;
  }
  const found = ratings.find((r) => r.INVESTMENT_GRADE === false && r.CREDIT_RATING === value);
  return found ? 'Sub-IG' : value;
}

const ISSUER_RATING_AGENCIES: (keyof IssuerRating)[] = ['MOODYS', 'SANDP', 'FITCH', 'KROLL'];

const STANDARD_RATING_FILTER_BUCKETS = new Set<string>(['', ...STANDARD_RATING_FILTER_SUFFIX]);

/** Canonical filter value: display alias VAR → stored value Various. */
function normalizeRatingFilterLabel(label: string): string {
  return label === 'VAR' ? 'Various' : label;
}

function isGenericRatingFilterLabel(label: string): boolean {
  const normalized = normalizeRatingFilterLabel(label);
  return STANDARD_RATING_FILTER_BUCKETS.has(normalized);
}

function findRatingAcrossAgencies(
  label: string,
  issuerRatings: IssuerRating | undefined,
): Rating | undefined {
  if (!issuerRatings || !label) {
    return undefined;
  }
  for (const agencyKey of ISSUER_RATING_AGENCIES) {
    const match = issuerRatings[agencyKey].find((rating) => rating.CREDIT_RATING === label);
    if (match) {
      return match;
    }
  }
  return undefined;
}

function findOrderNumForLabel(
  label: string,
  issuerRatings: IssuerRating | undefined,
): number | undefined {
  return findRatingAcrossAgencies(label, issuerRatings)?.ORDER_NUM;
}

function isNonInvestmentGradeRatingLabel(label: string, issuerRatings: IssuerRating): boolean {
  if (label === 'Sub-IG') {
    return true;
  }
  if (!label || isGenericRatingFilterLabel(label)) {
    return false;
  }
  return findRatingAcrossAgencies(label, issuerRatings)?.INVESTMENT_GRADE === false;
}

/** Map any agency label to the Moody's filter label at the same ORDER_NUM (or Sub-IG). */
function toCombinedRatingFilterLabel(label: string, issuerRatings: IssuerRating): string {
  const normalized = normalizeRatingFilterLabel(label);
  if (isGenericRatingFilterLabel(normalized)) {
    return normalized;
  }
  const orderNum = findOrderNumForLabel(label, issuerRatings);
  if (orderNum === undefined) {
    return normalized;
  }
  const moodysAtLevel = issuerRatings.MOODYS.find((rating) => rating.ORDER_NUM === orderNum);
  if (moodysAtLevel) {
    return getIssuerRatingLabel(issuerRatings.MOODYS, moodysAtLevel.CREDIT_RATING);
  }
  if (isNonInvestmentGradeRatingLabel(label, issuerRatings)) {
    return 'Sub-IG';
  }
  return label;
}

/** Same credit score (ORDER_NUM) across agencies; case-sensitive label comparison. */
export function ratingsAreEquivalent(
  rowLabel: string,
  selectedLabel: string,
  issuerRatings: IssuerRating,
): boolean {
  rowLabel = normalizeRatingFilterLabel(rowLabel);
  selectedLabel = normalizeRatingFilterLabel(selectedLabel);

  if (rowLabel === selectedLabel) {
    return true;
  }

  if (rowLabel === 'Sub-IG' || selectedLabel === 'Sub-IG') {
    return (
      isNonInvestmentGradeRatingLabel(rowLabel, issuerRatings) &&
      isNonInvestmentGradeRatingLabel(selectedLabel, issuerRatings)
    );
  }

  if (isGenericRatingFilterLabel(rowLabel) || isGenericRatingFilterLabel(selectedLabel)) {
    return rowLabel === selectedLabel;
  }

  const rowOrderNum = findOrderNumForLabel(rowLabel, issuerRatings);
  const selectedOrderNum = findOrderNumForLabel(selectedLabel, issuerRatings);
  return (
    rowOrderNum !== undefined &&
    selectedOrderNum !== undefined &&
    rowOrderNum === selectedOrderNum
  );
}

/** Combined filter options: Moody's IG ratings + standard buckets; row match still uses all agencies. */
export function getCombinedIssuerCreditRatingFilterOptions(
  issuerRatings: IssuerRating | undefined,
  extraLabels?: Iterable<string>,
): string[] {
  const combinedRatingLabels = new Set<string>(
    issuerRatings
      ? getIssuerCreditRatingFilterOptions(issuerRatings.MOODYS)
      : getIssuerCreditRatingFilterOptions([]),
  );

  if (extraLabels) {
    for (const extraLabel of extraLabels) {
      combinedRatingLabels.add(
        issuerRatings ? toCombinedRatingFilterLabel(extraLabel, issuerRatings) : extraLabel,
      );
    }
  }

  return sortRatingFilterOptions([...combinedRatingLabels], issuerRatings);
}

/** Sort filter model / pill values (same order as filter options). */
export function orderCombinedRatingFilterValues(
  values: string[],
  issuerRatings?: IssuerRating,
): string[] {
  return sortRatingFilterOptions([...values], issuerRatings);
}

function sortRatingFilterOptions(
  options: string[],
  issuerRatings: IssuerRating | undefined,
): string[] {
  const moodysRatings = issuerRatings?.MOODYS ?? [];
  const maxMoodysOrder = moodysRatings.reduce(
    (maxOrder, rating) => Math.max(maxOrder, rating.ORDER_NUM),
    0,
  );
  const suffixOrder = new Map(
    STANDARD_RATING_FILTER_SUFFIX.map((label, index) => [
      label,
      maxMoodysOrder + 1 + index,
    ]),
  );

  return [...new Set(options)].sort((leftLabel, rightLabel) => {
    const rank = (label: string): number => {
      if (label === '') {
        return -1;
      }
      const moodysMatch = moodysRatings.find((rating) => rating.CREDIT_RATING === label);
      return (
        moodysMatch?.ORDER_NUM ??
        suffixOrder.get(label as (typeof STANDARD_RATING_FILTER_SUFFIX)[number]) ??
        Number.MAX_SAFE_INTEGER
      );
    };

    return rank(leftLabel) - rank(rightLabel);
  });
}

export function getRowRatingLabels(
  data: {
    DEAL_MOODYS_RATING?: string;
    DEAL_SP_RATING?: string;
    DEAL_FITCH_RATING?: string;
    DEAL_KROLL_RATING?: string;
  },
  issuerRatings: IssuerRating,
): string[] {
  const { MOODYS, SANDP, FITCH, KROLL } = issuerRatings;
  return [
    getIssuerRatingLabel(MOODYS, data.DEAL_MOODYS_RATING ?? ''),
    getIssuerRatingLabel(SANDP, data.DEAL_SP_RATING ?? ''),
    getIssuerRatingLabel(FITCH, data.DEAL_FITCH_RATING ?? ''),
    getIssuerRatingLabel(KROLL, data.DEAL_KROLL_RATING ?? ''),
  ];
}

export interface SeriesRatingRow {
  SERIES_MOODYS_RATING?: string;
  SERIES_SANDP_RATING?: string;
  SERIES_FITCH_RATING?: string;
  SERIES_KROLL_RATING?: string;
}

/** Flatten all agency rating labels across every active series on a deal. */
export function getSeriesRatingLabels(
  seriesList: SeriesRatingRow[],
  issuerRatings: IssuerRating,
): string[] {
  const { MOODYS, SANDP, FITCH, KROLL } = issuerRatings;
  const labels: string[] = [];
  for (const series of seriesList) {
    labels.push(
      getIssuerRatingLabel(MOODYS, series.SERIES_MOODYS_RATING ?? ''),
      getIssuerRatingLabel(SANDP, series.SERIES_SANDP_RATING ?? ''),
      getIssuerRatingLabel(FITCH, series.SERIES_FITCH_RATING ?? ''),
      getIssuerRatingLabel(KROLL, series.SERIES_KROLL_RATING ?? ''),
    );
  }
  return labels;
}

function hasAnyRatingLabel(labels: string[]): boolean {
  return labels.some((label) => label !== '');
}

export function buildSeriesRatingsByDealId(
  rows: Array<SeriesRatingRow & { DEAL_ID?: string | number }>,
): Map<string, SeriesRatingRow[]> {
  const map = new Map<string, SeriesRatingRow[]>();
  for (const row of rows) {
    if (row.DEAL_ID == null) {
      continue;
    }
    const dealId = String(row.DEAL_ID);
    const existing = map.get(dealId) ?? [];
    existing.push({
      SERIES_MOODYS_RATING: row.SERIES_MOODYS_RATING,
      SERIES_SANDP_RATING: row.SERIES_SANDP_RATING,
      SERIES_FITCH_RATING: row.SERIES_FITCH_RATING,
      SERIES_KROLL_RATING: row.SERIES_KROLL_RATING,
    });
    map.set(dealId, existing);
  }
  return map;
}

export function rowMatchesCombinedSeriesRatingFilter(
  selectedLabels: Iterable<string>,
  dealId: string | number | undefined,
  seriesByDealId: Map<string, SeriesRatingRow[]>,
  dealLevelData: {
    DEAL_MOODYS_RATING?: string;
    DEAL_SP_RATING?: string;
    DEAL_FITCH_RATING?: string;
    DEAL_KROLL_RATING?: string;
  },
  issuerRatings: IssuerRating,
): boolean {
  const selected = [...selectedLabels];
  if (!selected.length) {
    return false;
  }

  const seriesList = dealId != null ? seriesByDealId.get(String(dealId)) : undefined;
  const dealLabels = getRowRatingLabels(dealLevelData, issuerRatings);
  const seriesLabels = seriesList?.length
    ? getSeriesRatingLabels(seriesList, issuerRatings)
    : [];

  const labelsToMatch =
    seriesList?.length && hasAnyRatingLabel(seriesLabels)
      ? [...dealLabels, ...seriesLabels]
      : dealLabels;

  return labelsToMatch.some((rowLabel) =>
    selected.some((selectedLabel) =>
      ratingsAreEquivalent(rowLabel, selectedLabel, issuerRatings),
    ),
  );
}

/** Deal statuses considered inactive for display/filtering. */
const DEAL_STATUSES_INACTIVE: ReadonlySet<string> = new Set([
  DealStatusEnum.EXPECTED,
  DealStatusEnum.DAY_TO_DAY,
  DealStatusEnum.WEEK_OF,
  DealStatusEnum.ANNOUNCED,
  DealStatusEnum.PRICE_IDEAS,
]);

export const getNormalizedDealStatus = (status: string | null | undefined): string => {
  if (!status) return '';
  const trimmedStatus = status.trim();
  return trimmedStatus.toLowerCase() === LEGACY_NOT_PUBLISHED_STATUS.toLowerCase()
    ? DealStatusEnum.EXPECTED
    : trimmedStatus;
};

export const getIsDealInactive = (status: string): boolean =>
  DEAL_STATUSES_INACTIVE.has(getNormalizedDealStatus(status));

/**
 * COUNTERPARTY_ELIGIBILITY format from server:
 * user-specific boolean derived by datasource.
 */
export const hasEligibleCounterpartyForCurrentUser = (
  counterpartyEligibility: boolean | null | undefined,
): boolean => {
  return counterpartyEligibility === true;
};



/** True when submitted equals Par ($M) column amount (tolerant for numeric noise). */
export function submittedMatchesParColumn(
  submitted: number,
  dealSizeRaw: number | string | null | undefined,
): boolean {
  return totalMatchesParColumn(submitted, dealSizeRaw);
}

export type MyCpQtyBucketsNumeric = { submitted: number; draft: number };

/** Builds submitted/draft buckets from enriched numeric fields (Genesis BIGDECIMAL → number | string). */
export function toMyCpQtyBuckets(
  submittedRaw: number | string | null | undefined,
  draftRaw: number | string | null | undefined,
): MyCpQtyBucketsNumeric | null {
  const submitted = Number(submittedRaw ?? 0);
  const draft = Number(draftRaw ?? 0);
  if (!Number.isFinite(submitted) || !Number.isFinite(draft)) return null;
  if (submitted === 0 && draft === 0) return null;
  return { submitted, draft };
}

/** Icon column state for My orders quantity column. */
export type MyCpOrderQtyDisplayKind = 'empty' | 'all_submitted' | 'all_draft' | 'mixed';

/** Locale string for order qty pills (full value also used for native `title` when truncated). */
export function formatCpQtyForPillDisplay(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

/** Parses ORDER_DISPLAY_QTY for pill display (per-order max aggregated on server). */
export function parseMyCpOrderDisplayQty(
  displayQtyRaw: number | string | null | undefined,
): number | null {
  const displayQty = Number(displayQtyRaw ?? NaN);
  if (!Number.isFinite(displayQty) || displayQty <= 0) {
    return null;
  }
  return displayQty;
}

/** Same basis as deals grid Par ($M) column: `DEAL_SIZE / 1000`. */
export function parColumnAmountFromDealSize(dealSizeRaw: number | string | null | undefined): number {
  const dealSize = Number(dealSizeRaw ?? NaN);
  if (!Number.isFinite(dealSize)) return NaN;
  return dealSize / 1000;
}

/** True when total (submitted + draft) equals Par ($M) column amount (tolerant for numeric noise). */
export function totalMatchesParColumn(
  total: number,
  dealSizeRaw: number | string | null | undefined,
): boolean {
  const parCol = parColumnAmountFromDealSize(dealSizeRaw);
  if (!Number.isFinite(total) || !Number.isFinite(parCol)) return false;
  const diff = Math.abs(total - parCol);
  const scale = Math.max(Math.abs(total), Math.abs(parCol), 1);
  return diff <= Math.max(1e-6, scale * 1e-9);
}

/** Green pill when total qty > 0 and every order is in success status; amber when any non-success order. */
export function isMyCpOrderPillGreen(
  buckets: MyCpQtyBucketsNumeric,
  nonSuccessOrderCountRaw?: number | string | null,
): boolean {
  const nonSuccessCount = Number(nonSuccessOrderCountRaw ?? 0);
  if (!Number.isFinite(nonSuccessCount) || nonSuccessCount > 0) {
    return false;
  }
  return buckets.submitted + buckets.draft > 0;
}

export function classifyMyCpOrderQtyDisplay(buckets: MyCpQtyBucketsNumeric | null): MyCpOrderQtyDisplayKind {
  if (!buckets) return 'empty';
  const { submitted, draft } = buckets;
  if (submitted <= 0 && draft <= 0) return 'empty';
  if (submitted > 0 && draft <= 0) return 'all_submitted';
  if (submitted <= 0 && draft > 0) return 'all_draft';
  return 'mixed';
}

/**
 * Tooltip for My orders pill:
 * - No tooltip: green pill (total qty > 0, no non-success orders).
 * - Otherwise: show Submitted / Draft breakdown.
 */
export function getMyCpOrderQtyCellTooltip(
  submittedRaw: number | string | null | undefined,
  draftRaw: number | string | null | undefined,
  nonSuccessOrderCountRaw?: number | string | null,
): string {
  const buckets = toMyCpQtyBuckets(submittedRaw, draftRaw);
  if (!buckets) return '';

  const kind = classifyMyCpOrderQtyDisplay(buckets);
  if (kind === 'empty') return '';

  const subFmt = formatCpQtyForPillDisplay(buckets.submitted);
  const draftFmt = formatCpQtyForPillDisplay(buckets.draft);
  const breakdown = `Submitted: ${subFmt} · Draft: ${draftFmt}`;

  // All draft: always show breakdown on hover (same shape as mixed).
  if (kind === 'all_draft') return breakdown;

  // Fully green pill: suppress redundant tooltip.
  if (isMyCpOrderPillGreen(buckets, nonSuccessOrderCountRaw)) {
    return '';
  }

  return breakdown;
}
